import _ from 'lodash'
import { filter, flow, first, defaultTo, isArray, isString } from 'lodash/fp'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { FormFeedback } from 'reactstrap'

import { isSubmitting } from 'e-submission/apps/selectors'
import FormGroup from 'e-submission/components/Layout/FormGroup'
import { isEmpty } from 'e-submission/utils/pattern'
import { useLocation } from 'react-router-dom'

const addFormGroup = (_Component) => {
  let Component
  try {
    Component = _Component((i) => i)
  } catch (error) {
    Component = _Component
  }
  return function WithFormGroup(props) {
    const {
      errorMessages,
      requiredMessages,
      warningMessages,
      infoMessages,
      id,
      label,
      fullLabel,
      fullLabelToggleTexts,
      isShowFullLabel,
      toggleFullLabel,
      styleOptions,
      invalidLabel,
      useLocalFormValidation,
      warningMessage,
      value,
      ...rest
    } = props
    const location = useLocation()

    const { messages = [], formColor } = useMemo(
      () =>
        flow(
          filter(({ messages }) => _.size(messages) > 0),
          first,
          defaultTo({})
        )([
          { messages: requiredMessages, formColor: 'danger' },
          { messages: warningMessages, formColor: 'warning' },
          { messages: errorMessages, formColor: 'danger' },
        ]),
      [errorMessages, requiredMessages, warningMessages]
    )

    const infoMessage = props.infoMessage || first(infoMessages)

    const submitting = useSelector(isSubmitting)
    const prevIsSubmitting = useRef(submitting)
    const [validated, setValidated] = useState(submitting)

    useEffect(() => {
      if (location.pathname === '/e-submission/form/submit') {
        setValidated(false)
      }
    }, [location.pathname])

    useEffect(() => {
      if (!prevIsSubmitting.current && submitting && location.pathname !== '/e-submission/form/submit') {
        setValidated(true)
      }
      prevIsSubmitting.current = submitting
    }, [submitting])

    // keep showing validation on focus when field already has value (matches old FormEngine behavior)
    const validate = (nextValidated = true) => {
      if (!nextValidated && !isEmpty(value)) return
      setValidated(nextValidated)
    }

    let Label
    if (isString(label)) {
      const labelText = !isShowFullLabel ? label : fullLabel
      Label = <label dangerouslySetInnerHTML={{ __html: labelText }} />
    } else if (isArray(label)) {
      Label = (
        <ul className="formEngineUl">
          {label.map((value, index) => (
            <li key={index}>{value}</li>
          ))}
        </ul>
      )
    }

    let isValidated = validated
    const regexCheckInsurePayerBeneficiary = /^(insured|payer|beneficiary).*(lastName|birthdate)$/
    if (regexCheckInsurePayerBeneficiary.test(id) && !_.isEmpty(value)) {
      isValidated = true
    }

    const toggleText = fullLabel
      ? !isShowFullLabel
        ? _.get(fullLabelToggleTexts, 'toShow')
        : _.get(fullLabelToggleTexts, 'toHide')
      : null

    const shouldShowMessage = validated || (!_.isEmpty(props.value) && !_.isEmpty(messages)) || isValidated // && location.pathname !== '/e-submission/form/submit'

    const {
      isSubmitting: _isSubmitting,
      errorMessages: _e,
      requiredMessages: _r,
      warningMessages: _w,
      ...componentProps
    } = rest

    return (
      <FormGroup id={id} {...styleOptions} color={shouldShowMessage ? formColor : ''}>
        {Label}
        {toggleText ? (
          <span className="full-label-toggle" onClick={toggleFullLabel}>
            {toggleText}
          </span>
        ) : null}
        {infoMessage && id === 'ATP.submit' && (
          <FormFeedback className="info-message attached-icon atp-review-page" style={{ marginBottom: '10px' }}>
            <p>{infoMessage}</p>
          </FormFeedback>
        )}
        {useLocalFormValidation ? (
          <Component {...componentProps} value={value} isValid={isEmpty(messages)} validate={validate} />
        ) : (
          <Component {...componentProps} value={value} validate={validate} />
        )}
        {shouldShowMessage &&
          _.map(messages, (message, index) => (
            <FormFeedback className="validation-message attached-icon" key={index}>
              <p>{message}</p>
            </FormFeedback>
          ))}
        {warningMessage && (
          <FormFeedback className="warning-message attached-icon">
            <p>{warningMessage}</p>
          </FormFeedback>
        )}
        {infoMessage && id !== 'ATP.submit' && (
          <FormFeedback className={`info-message attached-icon ${componentProps?.customStyleInfoMessage}`}>
            <p>{infoMessage}</p>
          </FormFeedback>
        )}
      </FormGroup>
    )
  }
}

export default addFormGroup
